import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { X } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import React, { CSSProperties, useContext } from "react";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import SelectAllBotsDropdown from "features/bots/MyBots/SelectAllBotsDropdown";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH, SMALL_MOBILE } from "common/constants";
import VerticalDivider from "common/misc/VerticalDivider";
import Dropdown from "common/Dropdown/Dropdown";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { getBulkActionButtonId } from "testIds.ts";

interface BulkActionsProps {
  actionButtonsData?: {
    onClick: () => void;
    disabled?: boolean;
    text: string;
    isLoading?: boolean;
  }[];
  unselectAll: () => void;
  selectAll?: () => void;
  numSelected: number;
  isAllSelected?: boolean;
  onCancel?: () => void;
  style?: CSSProperties;
  isBots?: boolean;
}

const BulkActions = ({
  actionButtonsData,
  unselectAll,
  selectAll,
  numSelected,
  isAllSelected,
  onCancel,
  isBots,
  style,
}: BulkActionsProps) => {
  const theme = useTheme();
  const { isStatusDropdownEnabled } = useContext(BotsBulkActionsContext) || {};
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const forceActionDropdown = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [true],
    false
  );
  const isSmallMobile = useMedia([`(max-width: 390px)`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const getActionButtons = () => {
    return actionButtonsData?.map((buttonData, index) => {
      const style: CSSProperties =
        index === actionButtonsData.length - 1 ? {} : { marginRight: 8 };
      if (buttonData.disabled) {
        style.opacity = 0.4;
      }
      return (
        <Button
          data-testid={getBulkActionButtonId(buttonData.text)}
          onClick={buttonData.onClick}
          key={buttonData.text}
          loading={buttonData.isLoading}
          style={style}
        >
          {buttonData.text}
        </Button>
      );
    });
  };

  const getActionDropdown = () => {
    const menuItems = actionButtonsData?.map((buttonData) => ({
      key: buttonData.text,
      label: buttonData.text,
      onClick: buttonData.onClick,
      className: buttonData.disabled ? "invalid" : undefined,
    }));

    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        title="Bulk Actions"
      >
        <StyledLinkText style={{ color: theme.colors.primaryBlue }}>
          Actions
        </StyledLinkText>
      </Dropdown>
    );
  };

  const getActions = () => {
    if (
      (isMobile && actionButtonsData && actionButtonsData.length > 1) ||
      forceActionDropdown
    )
      return getActionDropdown();
    return getActionButtons();
  };

  const getSelectAllButton = () => {
    const trigger = (
      <StyledLinkText
        onClick={() => {
          if (!isBots || !isStatusDropdownEnabled) {
            isAllSelected ? unselectAll?.() : selectAll?.();
          }
        }}
        style={{ color: theme.colors.primaryBlue }}
      >
        {isAllSelected ? "Unselect all" : "Select all"}
      </StyledLinkText>
    );
    if (isBots && isStatusDropdownEnabled)
      return <SelectAllBotsDropdown trigger={trigger} />;
    return trigger;
  };

  return (
    <BulkActionsWrapper style={style} $isBottomNavigator={isBottomNavigator}>
      <StyledLinkText onClick={() => unselectAll()}>
        {numSelected} Selected{" "}
        <LookIcon
          classic={<CloseOutlined />}
          newLook={
            <X size={14} strokeWidth={1.75} style={{ verticalAlign: -2 }} />
          }
        />
      </StyledLinkText>
      <VerticalDivider $isSmallMobile={isSmallMobile} />
      {getSelectAllButton()}
      <VerticalDivider $isSmallMobile={isSmallMobile} />
      {getActions()}
      <VerticalDivider $isSmallMobile={isSmallMobile} />
      <StyledLinkText
        onClick={onCancel}
        style={{ color: theme.colors.primaryBlue }}
      >
        Cancel
      </StyledLinkText>
    </BulkActionsWrapper>
  );
};

export default BulkActions;

const BulkActionsWrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  display: flex;
  align-items: center;
  ${(p) =>
    p.$isBottomNavigator &&
    `
    padding: 8px 16px;
    border-top: ${p.theme.borders.light};
    background-color: ${p.theme.colors.layoutBgColor};
  `};
`;
const StyledLinkText = styled(Text)`
  cursor: pointer;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
  user-select: none;
`;
